import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Dialog,
  DialogContentContainer,
  Search,
  Loader,
  Text,
  Button,
  Icon,
} from '@vibe/core';
import { Person, DropdownChevronDown } from '@vibe/icons';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PickedUser {
  id: string;
  name: string;
  email: string;
}

/** Internal type that includes the avatar URL from monday.com */
interface DisplayUser extends PickedUser {
  photoUrl?: string;
}

interface UserPickerProps {
  /** Currently selected user (null if nothing selected) */
  value: PickedUser | null;
  /** Called when the user picks a monday.com user */
  onChange: (user: PickedUser | null) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** User IDs to exclude from the dropdown (e.g. already-added users) */
  excludeUserIds?: string[];
}

// ─── GraphQL ──────────────────────────────────────────────────────────────────

const USERS_QUERY = `
  query GetUsers($limit: Int!, $page: Int!) {
    users(kind: non_guests, limit: $limit, page: $page) {
      id
      name
      email
      photo_thumb_small
    }
  }
`;

const DEFAULT_LIMIT = 10;
const SEARCH_LIMIT = 100;
const SEARCH_MIN_CHARS = 2;
const MAX_RESULTS = 20;
const DEBOUNCE_MS = 300;

// ─── Component ────────────────────────────────────────────────────────────────

const UserPicker: React.FC<UserPickerProps> = ({
  value,
  onChange,
  placeholder = 'Search for a user…',
  disabled = false,
  excludeUserIds = [],
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DisplayUser[]>([]);
  const [defaultUsers, setDefaultUsers] = useState<DisplayUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);

  const excludeSet = useMemo(
    () => new Set(excludeUserIds),
    [excludeUserIds],
  );

  // Load default users on mount
  useEffect(() => {
    let cancelled = false;

    async function loadDefaults() {
      setIsLoadingDefaults(true);
      try {
        const response = await monday.api(USERS_QUERY, {
          variables: { limit: DEFAULT_LIMIT, page: 1 },
        });
        const data = response.data as {
          users?: Array<{
            id: number | string;
            name: string;
            email: string;
            photo_thumb_small?: string;
          }>;
        };
        if (!cancelled) {
          setDefaultUsers(
            (data.users ?? []).map((u) => ({
              id: String(u.id),
              name: u.name,
              email: u.email,
              photoUrl: u.photo_thumb_small,
            })),
          );
        }
      } catch {
        // Silently fail
      } finally {
        if (!cancelled) setIsLoadingDefaults(false);
      }
    }

    loadDefaults();
    return () => {
      cancelled = true;
    };
  }, []);

  // Search users when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < SEARCH_MIN_CHARS) {
      setResults([]);
      return;
    }

    let cancelled = false;

    async function search() {
      setIsSearching(true);
      setSearchError(null);
      try {
        const response = await monday.api(USERS_QUERY, {
          variables: { limit: SEARCH_LIMIT, page: 1 },
        });
        const data = response.data as {
          users?: Array<{
            id: number | string;
            name: string;
            email: string;
            photo_thumb_small?: string;
          }>;
        };

        if (cancelled) return;

        const lowerQ = debouncedQuery.toLowerCase();
        const matched: DisplayUser[] = [];

        for (const u of data.users ?? []) {
          if (
            u.name.toLowerCase().includes(lowerQ) ||
            u.email.toLowerCase().includes(lowerQ)
          ) {
            matched.push({
              id: String(u.id),
              name: u.name,
              email: u.email,
              photoUrl: u.photo_thumb_small,
            });
          }
          if (matched.length >= MAX_RESULTS) break;
        }

        setResults(matched);
      } catch (err) {
        if (!cancelled) {
          setSearchError(
            err instanceof Error ? err.message : 'Search failed',
          );
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }

    search();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const handleSelect = useCallback(
    (user: PickedUser) => {
      onChange(user);
      setQuery('');
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery('');
  }, [onChange]);

  // Determine which items to show
  const isSearchMode = debouncedQuery.length >= SEARCH_MIN_CHARS;
  const displayItems = useMemo(() => {
    const items = isSearchMode ? results : defaultUsers;
    return excludeSet.size > 0
      ? items.filter((u) => !excludeSet.has(u.id))
      : items;
  }, [isSearchMode, results, defaultUsers, excludeSet]);

  // Trigger label
  const triggerLabel = value ? value.name : placeholder;

  return (
    <div className="rp-user-picker" ref={dialogRef}>
      <Dialog
        zIndex={1000}
        content={
          <DialogContentContainer
            className="rp-user-picker__dialog-content"
            size={DialogContentContainer.sizes.MEDIUM}
          >
            <div className="rp-user-picker__search">
              <Search
                size="small"
                value={query}
                onChange={(value: string) => setQuery(value)}
                placeholder="Type to search users…"
              />
            </div>

            <div className="rp-user-picker__list">
              {/* Loading */}
              {(isSearching || isLoadingDefaults) && (
                <div className="rp-user-picker__list-status">
                  <Loader size={Loader.sizes.XS} />
                  <span>{isSearchMode ? 'Searching…' : 'Loading users…'}</span>
                </div>
              )}

              {/* Error */}
              {searchError && (
                <div className="rp-user-picker__list-status">
                  <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
                    {searchError}
                  </Text>
                </div>
              )}

              {/* Hint */}
              {!isSearching &&
                !isLoadingDefaults &&
                !isSearchMode &&
                defaultUsers.length > 0 && (
                  <Text
                    type={Text.types.TEXT2}
                    className="rp-user-picker__list-hint"
                  >
                    Recent users · type 2+ characters to search
                  </Text>
                )}

              {/* No results */}
              {!isSearching &&
                !isLoadingDefaults &&
                !searchError &&
                isSearchMode &&
                displayItems.length === 0 && (
                  <div className="rp-user-picker__list-status">
                    <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                      No users found
                    </Text>
                  </div>
                )}

              {/* User items */}
              {displayItems.map((user) => (
                <button
                  key={user.id}
                  className="rp-user-picker__option"
                  onClick={() => handleSelect(user)}
                  type="button"
                >
                  {user.photoUrl ? (
                    <Avatar
                      type="img"
                      src={user.photoUrl}
                      size="small"
                      ariaLabel={user.name}
                    />
                  ) : (
                    <Avatar
                      type="text"
                      text={user.name}
                      size="small"
                      ariaLabel={user.name}
                    />
                  )}
                  <div className="rp-user-picker__option-info">
                    <Text type={Text.types.TEXT2}>{user.name}</Text>
                    <Text
                      type={Text.types.TEXT2}
                      className="rp-user-picker__option-email"
                    >
                      {user.email}
                    </Text>
                  </div>
                </button>
              ))}
            </div>

            {/* Clear selection */}
            {value && (
              <div className="rp-user-picker__clear-row">
                <Button
                  size={Button.sizes.SMALL}
                  kind={Button.kinds.TERTIARY}
                  onClick={handleClear}
                >
                  Clear selection
                </Button>
              </div>
            )}
          </DialogContentContainer>
        }
        hideTrigger={['clickoutside', 'esckey']}
        showTrigger={['click']}
        position="bottom-start"
      >
        <Button
          size={Button.sizes.SMALL}
          kind={Button.kinds.TERTIARY}
          disabled={disabled}
          className="rp-user-picker__trigger"
        >
          <Icon
            icon={Person}
            iconSize={16}
            className="rp-user-picker__trigger-icon"
          />
          <span className="rp-user-picker__trigger-label">{triggerLabel}</span>
          <Icon
            icon={DropdownChevronDown}
            iconSize={16}
            className="rp-user-picker__trigger-chevron"
          />
        </Button>
      </Dialog>
    </div>
  );
};

export default UserPicker;